// useAccessToken — silent acquire with redirect fallback.
//
// ADR-038 D6: long deep-research streams (>5 min) need the API token acquired
// BEFORE starting the SSE request. Don't depend on mid-stream client reauth.
//
// 2026-05-04 fix: ``accounts`` from ``useMsal()`` is a fresh array reference
// on every render of msal-react's context consumer. When pages depend on
// ``acquireToken`` inside a ``useCallback``/``useEffect`` chain (e.g.
// ``useEffect([refresh])`` where ``refresh = useCallback([..., acquireToken])``),
// the unstable ``acquireToken`` ref causes effect re-fires on every state
// change → infinite re-fetch loop. Caught in admin-ui browser validation
// (17+ identical fetches on a single page mount → AAD ratelimit).
// Same fix here because chat-ui carries the same pattern.
//
// 2026-05-13 round 3 (browser-validation feedback on admin-ui):
// MSAL silent token acquisition runs in a hidden iframe pointed at the
// Entra ID endpoint. On a stale session / network glitch / third-party-
// cookie block the iframe fails to post back within MSAL's monitor
// window (~6s default) and the SDK throws ``BrowserAuthError`` with
// errorCode ``timed_out``. Pre-fix the catch block only handled
// ``InteractionRequiredAuthError`` and re-threw everything else — the
// rejection bubbled to ``unhandledrejection`` and surfaced as a red
// ``Uncaught (in promise) BrowserAuthError: timed_out`` in the console.
// New behaviour: an allowlist of silent-flow failure codes triggers
// the same fallback as InteractionRequired (escalate to redirect).
// Same fix as admin-ui because chat-ui carries the identical pattern.

import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useRef } from "react";
import { apiScopes } from "./msalConfig";

// MSAL BrowserAuthError codes that mean "silent iframe flow failed in a
// way a visible redirect can recover from". Mirrors the admin-ui list;
// keep the two in sync. See admin-ui/src/auth/useAccessToken.ts for the
// detailed rationale per code.
const SILENT_FALLBACK_ERROR_CODES = new Set<string>([
  "timed_out",
  "monitor_window_timeout",
  "iframe_closed_prematurely",
  "block_iframe_reload",
  "empty_window_error",
  "redirect_bridge_empty_response",
]);

function isSilentFlowFailure(err: unknown): err is BrowserAuthError {
  return err instanceof BrowserAuthError && SILENT_FALLBACK_ERROR_CODES.has(err.errorCode);
}

export function useAccessToken() {
  const { instance, accounts } = useMsal();

  // Mirror accounts into a ref so acquireToken can read the latest list
  // without forming a dep on the array reference (which churns).
  const accountsRef = useRef(accounts);
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const acquireToken = useCallback(async (): Promise<string> => {
    // F16/P0-3 fix: align with admin-ui — AuthProvider drives initial
    // login; useAccessToken NEVER triggers loginRedirect from here.
    // The prior chat-ui pattern (loginRedirect with API-only scope from
    // each useAccessToken consumer) raced with sibling consumers and
    // also lacked openid/profile/email — AAD bounced back to email entry.
    let account = instance.getActiveAccount();
    if (!account) {
      const fromRef = accountsRef.current;
      if (fromRef.length > 0) {
        account = fromRef[0];
      } else {
        const fromInstance = instance.getAllAccounts();
        if (fromInstance.length > 0) {
          account = fromInstance[0];
        }
      }
    }
    if (!account) {
      throw new Error("useAccessToken called with no MSAL account. AuthProvider should have driven login first.");
    }
    try {
      const result = await instance.acquireTokenSilent({ account, scopes: apiScopes });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Trigger redirect; this will navigate away.
        await instance.acquireTokenRedirect({ account, scopes: apiScopes });
        throw new Error("redirecting for token refresh");
      }
      if (isSilentFlowFailure(err)) {
        // Hidden-iframe silent flow failed (timeout, premature close,
        // blocked reload, empty response). Recoverable by escalating
        // to the visible redirect flow — same as InteractionRequired.
        // Pre-fix this branch re-threw and surfaced as an uncaught
        // promise rejection in the browser console.
        // eslint-disable-next-line no-console
        console.warn(`[useAccessToken] silent token acquisition failed (${err.errorCode}); escalating to redirect`);
        await instance.acquireTokenRedirect({ account, scopes: apiScopes });
        throw new Error(`redirecting for token refresh after silent failure: ${err.errorCode}`);
      }
      throw err;
    }
    // ``instance`` is a singleton from the MsalProvider; ``accountsRef``
    // is a stable ref. This useCallback returns the SAME reference for
    // the lifetime of the component.
  }, [instance]);

  return { acquireToken, accounts };
}
